import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Main as Container } from 'Components/Main';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../../store/auth';
import { options, typeLabelKeys } from '../../../../utils/labels_utils';
import { useQuery } from 'react-query';
import { getGapAnalysisControlsDetails, postGapAnalysisVinculo } from 'services/gap-analysis';
import { PageHelper } from '../../../../Components/PageHelper';
import { Spinner } from 'Components/common/Spinner';
import { PageHeader, Select, TextField } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const GREEN = 'var(--vo-success)';
const GREEN_DK = 'color-mix(in srgb, var(--vo-success) 80%, black)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';
const BORDER_IDLE = 'var(--vo-steel)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';

const StepperCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 24px 28px;
  margin-bottom: 20px;
`;
const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
`;
const StepCircleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`;
const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$done ? GREEN : p.$active ? BLUE : DIVIDER};
  color: ${p => (p.$done || p.$active) ? 'white' : BORDER_IDLE};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
`;
const StepLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: ${p => p.$active ? '600' : '400'};
  color: ${p => p.$done ? GREEN : p.$active ? BLUE : BORDER_IDLE};
  white-space: nowrap;
  text-align: center;
  max-width: 72px;
`;
const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${p => p.$done ? GREEN : DIVIDER};
  margin: 0 4px;
  margin-top: 15px;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 28px;
  margin-bottom: 16px;
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`;
const CardSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 20px;
`;

const SelectWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 340px;
  margin-bottom: 16px;
`;
const SelectLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;
const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;
const PanelLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 10px;
`;
const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 10px;
`;
const AvailList = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 10px;
  overflow: hidden;
  max-height: ${p => p.$tall ? '320px' : '280px'};
  overflow-y: auto;
`;
const SelList = styled.div`
  border: 1.5px solid ${p => p.$hasItems ? BLUE : BORDER};
  border-radius: 10px;
  overflow: hidden;
  min-height: 120px;
  background: var(--vo-capri-25);
  max-height: 320px;
  overflow-y: auto;
`;
const ARow = styled.div`
  display: flex;
  align-items: ${p => p.top ? 'flex-start' : 'center'};
  justify-content: space-between;
  padding: 11px 14px;
  border-bottom: 1px solid var(--vo-mist);
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const SRow = styled.div`
  display: flex;
  align-items: ${p => p.top ? 'flex-start' : 'center'};
  justify-content: space-between;
  padding: 11px 14px;
  border-bottom: 1px solid ${DIVIDER};
  &:last-child { border-bottom: none; }
`;
const IName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const IDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const ICode = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BORDER_IDLE};
  margin: 0 0 2px;
`;
const ICtrl = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  margin: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;
const RemBtn = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${BORDER_IDLE};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { color: var(--vo-danger); }
`;
const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
`;
const EmptyTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${BORDER_IDLE};
  font-style: italic;
  margin: 0;
`;

const SumCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 28px;
  max-width: 720px;
  margin-bottom: 16px;
`;
const SumTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 20px;
`;
const SumGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;
const SumLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 10px;
`;
const SumEntityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${BG};
  border-radius: 10px;
  margin-bottom: 6px;
`;
const SumEName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${INK};
`;
const TypeBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--vo-cobalto-8);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
`;
const SumCtrlCard = styled.div`
  padding: 10px 14px;
  background: ${BG};
  border-radius: 10px;
  margin-bottom: 6px;
`;

const NavRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;
const BackBtn = styled.button`
  height: 44px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: ${BG}; }
`;
const NextBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: ${BLUE_DEEP}; }
`;
const SaveBtn = styled.button`
  height: 44px;
  background: ${GREEN};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: ${GREEN_DK}; }
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BORDER_IDLE} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

export const GapAnalysisControlsLinkShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant.slug;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.pathname.startsWith('/compliance-program')
    ? '/compliance-program'
    : `/gap-analysis/show/${id}`;

  const [step, setStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState('companies');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  const [controlSearch, setControlSearch] = useState('');

  const tourSteps = useMemo(() => [
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step0'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="entity-type-select"]', content: t('gap-analysis:controlsLink.tour.step3'), disableScrolling: true },
    { target: '[data-tour="next-button"]', content: t('gap-analysis:controlsLink.tour.step5'), disableScrolling: true },
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step8'), placement: 'center' },
  ], [t]);

  const { isLoading, error, data: gapAnalysis } = useQuery(
    ['getGapAnalysisControlsDetails', id],
    async () => {
      const data = await getGapAnalysisControlsDetails(id);
      return data.status === 200 ? data.data : null;
    }
  );

  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || '',
      type: 'companies',
      entity: [],
      controls: [],
    }
  });

  useEffect(() => {
    if (!selectedOption || selectedOption === 'companies') {
      setItems(selectedTenant
        ? [{ _id: selectedTenant._id, name: selectedTenant.name, description: selectedTenant.description, type: 'companies' }]
        : []);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const selectedObj = options.find(o => o.value === selectedOption);
        if (!selectedObj?.fetchFunction) return;
        const response = await selectedObj.fetchFunction(tenant);
        setItems(response.data.map(item => ({ ...item, type: selectedObj.value })) || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedOption, tenant, selectedTenant]);

  const submitVinculos = handleSubmit(async (data) => {
    try {
      const formattedData = {
        ...data,
        entity: data.entity.map(item => ({ id: item._id, type: item.type })),
        controls: data.controls.map(item => ({ id: item._id, frameworksIds: item.frameworksIds })),
      };
      const { data: linkResult } = await postGapAnalysisVinculo(id, formattedData);
      navigate(backPath, { state: { linkResult } });
    } catch (err) {
      console.error('Erro ao salvar vínculos:', err);
      navigate(backPath, { state: { linkResult: { code: 'GA_LINK_ERROR' } } });
    }
  });

  if (isLoading) return <p>{t('gap-analysis:controlsLink.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;

  const controls = gapAnalysis?.controls || [];
  const selectedEntities = watch('entity') || [];
  const selectedControls = watch('controls') || [];

  const availableEntities = items
    .filter(i => !selectedEntities.find(e => e._id === i._id))
    .filter(i => !entitySearch || i.name?.toLowerCase().includes(entitySearch.toLowerCase()));

  const availableControls = controls
    .filter(c => !selectedControls.find(s => s._id === c._id))
    .filter(c => !controlSearch ||
      c.scfControl?.toLowerCase().includes(controlSearch.toLowerCase()) ||
      c.scfControlDescription?.toLowerCase().includes(controlSearch.toLowerCase()));

  const handleSelectEntity = (item) => setValue('entity', [...selectedEntities, item]);
  const handleRemoveEntity = (item) => setValue('entity', selectedEntities.filter(e => e._id !== item._id));
  const handleSelectControl = (ctrl) => setValue('controls', [...selectedControls, ctrl]);
  const handleRemoveControl = (ctrl) => setValue('controls', selectedControls.filter(c => c._id !== ctrl._id));

  const STEPS = [
    t('gap-analysis:controlsLink.steps.step1'),
    t('gap-analysis:controlsLink.steps.step2'),
    t('gap-analysis:controlsLink.steps.step3'),
  ];

  return (
    <>
      <PageHelper steps={tourSteps} tourKey="gap-analysis-controls-link-tour" autoStart={true} showButton={true} />
      <Container>
        <PageHeader
          title={t('gap-analysis:controlsLink.title')}
          segments={[
            { label: t('common:breadcrumbs.analysis') },
            { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
            ...(gapAnalysis?.gapAnalysis?.name
              ? [{ label: gapAnalysis.gapAnalysis.name, route: `/gap-analysis/show/${id}` }]
              : []),
            { label: t('gap-analysis:controlsLink.title') },
          ]}
        />

        <StepperCard>
          <StepperRow>
            {STEPS.map((name, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <React.Fragment key={i}>
                  {i > 0 && <StepLine $done={i <= step} />}
                  <StepCircleWrap>
                    <StepCircle $done={done} $active={active}>
                      {done
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        : i + 1}
                    </StepCircle>
                    <StepLabel $done={done} $active={active}>{name}</StepLabel>
                  </StepCircleWrap>
                </React.Fragment>
              );
            })}
          </StepperRow>
        </StepperCard>

        {step === 0 && (
          <Card>
            <CardTitle>{t('gap-analysis:controlsLink.entityStepTitle')}</CardTitle>
            <CardSub>{t('gap-analysis:controlsLink.entityStepSub')}</CardSub>

            <SelectWrap data-tour="entity-type-select">
              <SelectLabel>{t('gap-analysis:controlsLink.entityTypeLabel')}</SelectLabel>
              <Select
                name="entityType"
                value={selectedOption}
                onChange={value => {
                  setSelectedOption(value);
                  setValue('entity', []);
                  setEntitySearch('');
                }}
                options={options.map(opt => ({ value: opt.value, label: t(opt.labelKey) }))}
              />
            </SelectWrap>

            {loading && <Spinner />}

            <PickerGrid>
              <div>
                <PanelLabel>{t('gap-analysis:controlsLink.availableLabel')}</PanelLabel>
                <SearchWrap>
                  <TextField
                    fullWidth
                    placeholder={t('gap-analysis:controlsLink.searchEntityPlaceholder')}
                    value={entitySearch}
                    onChange={e => setEntitySearch(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingLeft: '38px' } }}
                  />
                  <SearchIcon />
                </SearchWrap>
                <AvailList>
                  {availableEntities.map(item => (
                    <ARow key={item._id} data-testid="available-entity-row" onClick={() => handleSelectEntity(item)}>
                      <div>
                        <IName>{item.name}</IName>
                        {item.description && <IDesc>{item.description}</IDesc>}
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={BORDER_IDLE} strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </ARow>
                  ))}
                  {availableEntities.length === 0 && (
                    <EmptyState><EmptyTxt>{t('gap-analysis:controlsLink.noEntitySelected')}</EmptyTxt></EmptyState>
                  )}
                </AvailList>
              </div>

              <div>
                <PanelLabel>
                  {t('gap-analysis:controlsLink.selectedLabel', { count: selectedEntities.length })}
                </PanelLabel>
                <div style={{ height: 50 }} />
                <SelList $hasItems={selectedEntities.length > 0}>
                  {selectedEntities.map(item => (
                    <SRow key={item._id}>
                      <div>
                        <IName>{item.name}</IName>
                        <IDesc>{typeLabelKeys[item.type] ? t(typeLabelKeys[item.type]) : item.type}</IDesc>
                      </div>
                      <RemBtn onClick={() => handleRemoveEntity(item)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </RemBtn>
                    </SRow>
                  ))}
                  {selectedEntities.length === 0 && (
                    <EmptyState><EmptyTxt>{t('gap-analysis:controlsLink.noEntitySelected')}</EmptyTxt></EmptyState>
                  )}
                </SelList>
              </div>
            </PickerGrid>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardTitle>{t('gap-analysis:controlsLink.controlsStepTitle')}</CardTitle>
            <CardSub>{t('gap-analysis:controlsLink.controlsStepSub')}</CardSub>
            <PickerGrid>
              <div>
                <PanelLabel>{t('gap-analysis:controlsLink.controlsAvailLabel')}</PanelLabel>
                <SearchWrap>
                  <TextField
                    fullWidth
                    placeholder={t('gap-analysis:controlsLink.searchControlPlaceholder')}
                    value={controlSearch}
                    onChange={e => setControlSearch(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingLeft: '38px' } }}
                  />
                  <SearchIcon />
                </SearchWrap>
                <AvailList $tall>
                  {availableControls.map(ctrl => (
                    <ARow key={ctrl._id} data-testid="available-control-row" top onClick={() => handleSelectControl(ctrl)}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                        <ICode>{ctrl.scfId}</ICode>
                        <ICtrl>{ctrl.scfControl}</ICtrl>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={BORDER_IDLE} strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </ARow>
                  ))}
                  {availableControls.length === 0 && (
                    <EmptyState><EmptyTxt>{t('gap-analysis:controlsLink.noControlSelected')}</EmptyTxt></EmptyState>
                  )}
                </AvailList>
              </div>

              <div>
                <PanelLabel>
                  {t('gap-analysis:controlsLink.selectedLabel', { count: selectedControls.length })}
                </PanelLabel>
                <div style={{ height: 50 }} />
                <SelList $hasItems={selectedControls.length > 0}>
                  {selectedControls.map(ctrl => (
                    <SRow key={ctrl._id} top>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                        <ICode>{ctrl.scfId}</ICode>
                        <ICtrl>{ctrl.scfControl}</ICtrl>
                      </div>
                      <RemBtn onClick={() => handleRemoveControl(ctrl)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </RemBtn>
                    </SRow>
                  ))}
                  {selectedControls.length === 0 && (
                    <EmptyState><EmptyTxt>{t('gap-analysis:controlsLink.noControlSelected')}</EmptyTxt></EmptyState>
                  )}
                </SelList>
              </div>
            </PickerGrid>
          </Card>
        )}

        {step === 2 && (
          <SumCard>
            <SumTitle>{t('gap-analysis:controlsLink.summaryTitle')}</SumTitle>
            <SumGrid>
              <div>
                <SumLabel>{t('gap-analysis:controlsLink.entitiesLabel')}</SumLabel>
                {selectedEntities.map((entity, idx) => (
                  <SumEntityRow key={idx}>
                    <SumEName>{entity.name}</SumEName>
                    <TypeBadge>{typeLabelKeys[entity.type] ? t(typeLabelKeys[entity.type]) : entity.type}</TypeBadge>
                  </SumEntityRow>
                ))}
                {selectedEntities.length === 0 && <EmptyTxt>{t('gap-analysis:controlsLink.noEntitySelected')}</EmptyTxt>}
              </div>
              <div>
                <SumLabel>{t('gap-analysis:controlsLink.controlsListLabel')}</SumLabel>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {selectedControls.map((ctrl, idx) => (
                    <SumCtrlCard key={idx}>
                      <ICode>{ctrl.scfId}</ICode>
                      <p style={{ fontFamily: "'Lato',sans-serif", fontSize: 13, color: INK, margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        {ctrl.scfControl}
                      </p>
                    </SumCtrlCard>
                  ))}
                  {selectedControls.length === 0 && <EmptyTxt>{t('gap-analysis:controlsLink.noControlSelected')}</EmptyTxt>}
                </div>
              </div>
            </SumGrid>
          </SumCard>
        )}

        <NavRow>
          {step > 0 && (
            <BackBtn onClick={() => setStep(s => s - 1)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              {t('gap-analysis:controlsLink.backButton')}
            </BackBtn>
          )}
          {step < STEPS.length - 1 && (
            <NextBtn data-tour="next-button" onClick={() => setStep(s => s + 1)}>
              {t('gap-analysis:controlsLink.nextButton')}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </NextBtn>
          )}
          {step === STEPS.length - 1 && (
            <SaveBtn onClick={submitVinculos}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {t('gap-analysis:controlsLink.saveButton')}
            </SaveBtn>
          )}
        </NavRow>
      </Container>
    </>
  );
};
